import React, { useEffect, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { UserCircle, EnvelopeSimple, Lock, CheckCircle } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const Section = ({ icon: Icon, title, description, children, testid }) => (
  <Card className="rounded-2xl p-6" data-testid={testid}>
    <div className="flex items-start gap-3 mb-5">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
        <Icon size={20} weight="duotone" className="text-primary" />
      </div>
      <div>
        <p className="font-heading font-medium text-lg leading-tight">{title}</p>
        {description && <p className="text-sm text-foreground/65 mt-0.5">{description}</p>}
      </div>
    </div>
    {children}
  </Card>
);

const AccountPage = () => {
  const { checkAuth, logout } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [emailInput, setEmailInput] = useState('');
  const [pwd, setPwd] = useState({ current_password: '', new_password: '', confirm: '' });
  const [requirements, setRequirements] = useState([]);
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [busy, setBusy] = useState('');

  const load = async () => {
    const r = await api.get('/api/me/account');
    setData(r.data);
    setForm({
      first_name: r.data.first_name, last_name: r.data.last_name, phone: r.data.phone,
      address: r.data.address, province: r.data.province, comune: r.data.comune, region: r.data.region,
    });
    setEmailInput(r.data.email || '');
  };

  useEffect(() => {
    load().catch(() => toast.error('Errore caricamento account'));
    api.get('/api/auth/password-requirements')
      .then((r) => setRequirements(r.data.requirements || []))
      .catch(() => {});
    api.get('/api/regions').then((r) => setRegions(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!form.region) { setProvinces([]); return; }
    api.get(`/api/zones?region=${encodeURIComponent(form.region)}`)
      .then((r) => setProvinces(r.data || [])).catch(() => {});
  }, [form.region]);

  const saveProfile = async () => {
    if (!form.first_name?.trim()) { toast.error('Il nome è obbligatorio'); return; }
    setBusy('profile');
    try {
      await api.put('/api/me/account', form);
      toast.success('Dati aggiornati');
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setBusy(''); }
  };

  const saveEmail = async () => {
    setBusy('email');
    try {
      const r = await api.put('/api/me/account/email', { email: emailInput.trim() });
      toast.success(r.data.verification_code_sent
        ? 'Email aggiornata: controlla la posta per il nuovo codice di verifica'
        : 'Email aggiornata: richiedi un nuovo codice di verifica dalla dashboard');
      await load();
      await checkAuth();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore modifica email');
    } finally { setBusy(''); }
  };

  const savePassword = async () => {
    if (pwd.new_password !== pwd.confirm) { toast.error('Le due password non coincidono'); return; }
    setBusy('password');
    try {
      await api.post('/api/me/account/password', {
        current_password: pwd.current_password, new_password: pwd.new_password,
      });
      setPwd({ current_password: '', new_password: '', confirm: '' });
      toast.success('Password aggiornata: accedi di nuovo con la nuova password');
      setTimeout(() => { logout(); }, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore cambio password');
    } finally { setBusy(''); }
  };

  if (!data) {
    return <div className="p-8 text-foreground/60" data-testid="account-loading">Caricamento account…</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-12 space-y-5" data-testid="account-page">
        <div className="mb-2">
          <h1 className="font-heading font-semibold text-4xl mb-2">Il mio account</h1>
          <p className="text-foreground/70">Dati di accesso, contatti e sicurezza del tuo profilo.</p>
        </div>

        <Section icon={UserCircle} title="Dati personali" description="Usati per contattarti e precompilare le richieste" testid="account-personal">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Nome</Label>
              <Input value={form.first_name || ''} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="account-first-name" />
            </div>
            <div>
              <Label>Cognome</Label>
              <Input value={form.last_name || ''} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="account-last-name" />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input type="tel" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="account-phone" />
            </div>
            <div>
              <Label>Indirizzo</Label>
              <Input value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Via, numero civico" data-testid="account-address" />
            </div>
            <div>
              <Label>Regione</Label>
              <Select value={form.region || ''} onValueChange={(v) => setForm({ ...form, region: v, province: '' })}>
                <SelectTrigger data-testid="account-region"><SelectValue placeholder="Scegli regione" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Provincia</Label>
              <Select value={form.province || ''} onValueChange={(v) => setForm({ ...form, province: v })} disabled={!form.region}>
                <SelectTrigger data-testid="account-province"><SelectValue placeholder="Scegli provincia" /></SelectTrigger>
                <SelectContent>{provinces.map((z) => <SelectItem key={z.zone_id} value={z.province}>{z.province}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Comune</Label>
              <Input value={form.comune || ''} onChange={(e) => setForm({ ...form, comune: e.target.value })} placeholder="Es. Roma" data-testid="account-comune" />
            </div>
          </div>
          <Button onClick={saveProfile} disabled={busy === 'profile'} className="rounded-full mt-5" data-testid="account-save-profile">
            Salva dati personali
          </Button>
        </Section>

        <Section icon={EnvelopeSimple} title="Email di accesso"
          description={data.email_verified
            ? 'Email verificata: per sicurezza non è più modificabile'
            : 'Puoi modificarla finché non hai verificato l\'indirizzo'}
          testid="account-email">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[240px]">
              <Label>Email</Label>
              <Input
                type="email"
                value={emailInput}
                disabled={data.email_verified}
                onChange={(e) => setEmailInput(e.target.value)}
                data-testid="account-email-input"
              />
            </div>
            {data.email_verified ? (
              <Badge className="bg-emerald-100 text-emerald-800 h-9 px-3 flex items-center gap-1.5" data-testid="account-email-verified-badge">
                <CheckCircle size={15} weight="fill" /> Email verificata
              </Badge>
            ) : (
              <Button onClick={saveEmail} disabled={busy === 'email' || !emailInput.trim()} className="rounded-full" data-testid="account-save-email">
                Aggiorna email
              </Button>
            )}
          </div>
          {!data.email_verified && (
            <p className="text-xs text-foreground/60 mt-3">
              Dopo la modifica invieremo il codice a 6 cifre al nuovo indirizzo. Una volta verificata,
              l'email resterà definitiva.
            </p>
          )}
        </Section>

        <Section icon={Lock} title="Password" description="Inserisci la password attuale per impostarne una nuova" testid="account-password">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label>Password attuale</Label>
              <Input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="account-current-password" />
            </div>
            <div>
              <Label>Nuova password</Label>
              <Input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="account-new-password" />
            </div>
            <div>
              <Label>Conferma nuova</Label>
              <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="account-confirm-password" />
            </div>
          </div>
          {requirements.length > 0 && (
            <ul className="text-xs text-foreground/60 mt-3 space-y-0.5">
              {requirements.map((r, i) => <li key={i}>• {r}</li>)}
            </ul>
          )}
          <Button onClick={savePassword} disabled={busy === 'password' || !pwd.current_password || !pwd.new_password}
            className="rounded-full mt-5" data-testid="account-save-password">
            Cambia password
          </Button>
          <p className="text-xs text-foreground/55 mt-3">
            Per sicurezza, al cambio password tutte le sessioni attive vengono chiuse (anche questa):
            dovrai accedere di nuovo con la nuova password. Riceverai un'email di conferma.
          </p>
        </Section>
      </div>
    </div>
  );
};

export default AccountPage;
